import { Box, Stack, SxProps } from '@mui/material';
import { useFormikContext } from 'formik';
import { getFixedT } from 'i18next';
import React, { useMemo } from 'react';

import { Criteria, FilterConfigFormat } from '../../../../../config';
import type { FilterValuesProps } from '../../../../../types';
import { FilterConfigCriteria, getFilterConfig } from '../../../../../utils/estateTypeIdUtils';
import { EstateSearchAutocompleteWrapper } from '../../../../EstateSearch/ImmobilienApi/EstateSearchAutocomplete/EstateSearchAutocompleteWrapper';
import { FromToFilter } from '../FromToFilter/FromToFilter';
import { MarketingTypeEstateTypeIdFilter } from '../MarketingTypeEstateTypeIdFilter/MarketingTypeEstateTypeIdFilter';
import { PerimeterFilter } from '../PerimeterFilter/PerimeterFilter';
import { RoomsFilter } from '../RoomsFilter/RoomsFilter';
import { styles } from './AllFilters.styles';

const t = getFixedT(null, 'core-immobilien');
const filterConfig: FilterConfigCriteria = getFilterConfig(FilterConfigFormat.Criteria) as FilterConfigCriteria;

interface Props {
  sx?: SxProps;
}
export const AllFilters: React.FC<Props> = ({ sx }) => {
  const { values } = useFormikContext<FilterValuesProps>();

  const singleFilterConfig = useMemo(
    () =>
      filterConfig?.find(
        (item) => item.marketingType === values.marketingType && item.estateTypeId === values.estateTypeId
      ),
    [values.marketingType, values.estateTypeId]
  );

  return (
    <Box sx={sx}>
      <Stack spacing={{ xs: 5, sm: 0 }} direction={{ xs: 'column', sm: 'row' }}>
        <EstateSearchAutocompleteWrapper />
        <Box sx={styles.perimeterWrapper}>
          <PerimeterFilter />
        </Box>
      </Stack>
      <MarketingTypeEstateTypeIdFilter />
      {singleFilterConfig?.fields?.map((field) => {
        switch (field) {
          case Criteria.PRICE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('priceFrom')}
                fieldNameFrom="minPrice"
                labelTo={t('priceTo')}
                fieldNameTo="maxPrice"
                replaceRegex={/[^0-9]/g}
                fieldType="price"
              />
            );
          case Criteria.MARKET_VALUE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('priceFrom')}
                fieldNameFrom="minMarketValue"
                labelTo={t('priceTo')}
                fieldNameTo="maxMarketValue"
                replaceRegex={/[^0-9]/g}
                fieldType="price"
              />
            );
          case Criteria.RENT:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('priceFrom')}
                fieldNameFrom="minRent"
                labelTo={t('priceTo')}
                fieldNameTo="maxRent"
                replaceRegex={/[^0-9]/g}
                fieldType="price"
              />
            );
          case Criteria.LEASE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('priceFrom')}
                fieldNameFrom="minLease"
                labelTo={t('priceTo')}
                fieldNameTo="maxLease"
                replaceRegex={/[^0-9]/g}
                fieldType="price"
              />
            );
          case Criteria.LIVING_SPACE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('livingSpaceFrom')}
                fieldNameFrom="minLivingSpace"
                labelTo={t('livingSpaceTo')}
                fieldNameTo="maxLivingSpace"
                replaceRegex={/[^0-9]/g}
                fieldType="livingSpace"
              />
            );
          case Criteria.PROPERTY_SIZE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('propertySizeFrom')}
                fieldNameFrom="minPropertySize"
                labelTo={t('propertySizeTo')}
                fieldNameTo="maxPropertySize"
                replaceRegex={/[^0-9]/g}
                fieldType="livingSpace"
              />
            );
          case Criteria.TOTAL_SPACE:
            return (
              <FromToFilter
                key={field}
                labelFrom={t('totalSpaceFrom')}
                fieldNameFrom="minTotalSpace"
                labelTo={t('totalSpaceTo')}
                fieldNameTo="maxTotalSpace"
                replaceRegex={/[^0-9]/g}
                fieldType="livingSpace"
              />
            );
          case Criteria.ROOMS:
            return <RoomsFilter key={field} />;
          default:
            return null;
        }
      })}
    </Box>
  );
};

AllFilters.displayName = 'AllFilters';
